import { CSSProperties, DragEvent, FC, Key, ReactNode, useCallback, useMemo, useState } from 'react';
import { Checkbox } from 'antd';
import { TbFolderCancel } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CloudAttachment } from '@/services';
import { DragHandle, DropPlaceholder, SyncingSmallTag } from '@/shared/components/drag-and-drop';
import {
    getDragActiveStyle,
    getDropZoneIdleStyle,
    getDropZoneStyle,
} from '@/shared/components/drag-and-drop/platformStyles';
import useNativeDropZone from '@/shared/components/drag-and-drop/useNativeDropZone';
import CompactCollapsible from '@/shared/components/layouts/sections/CompactCollapsible';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { WorkspaceFolderGlyph } from '@/shared/components/workspace-resources';

import { AttachmentFolderNode, getFolderFileIds, getFolderItemCount } from '../../attachmentTree';
import AttachmentItemActions from '../AttachmentItemActions';

interface AttachmentFolderSectionProps {
    canDropInFolder: (event: DragEvent<HTMLElement>, destinationFolder: CloudAttachment) => boolean;
    draggedAttachmentId?: string;
    draggedAttachmentHeight?: number;
    dropTargetFolderId?: string | null;
    folder: AttachmentFolderNode;
    level?: number;
    movingAttachmentIds?: ReadonlySet<string>;
    syncingFolderIds?: ReadonlySet<string>;
    onDragEnd: () => void;
    onDragStart: (event: DragEvent<HTMLElement>, attachment: CloudAttachment) => void;
    onDropFolder: (event: DragEvent<HTMLElement>, destinationFolder?: CloudAttachment) => void;
    onDropTargetChange: (destinationFolderId: string, active: boolean) => void;
    onSelectedKeysChange: (selectedKeys: ReadonlySet<Key>) => void;
    renderFiles: (files: CloudAttachment[]) => ReactNode;
    selectedKeys: ReadonlySet<Key>;
}

const AttachmentFolderSection: FC<AttachmentFolderSectionProps> = ({
    canDropInFolder,
    draggedAttachmentId,
    draggedAttachmentHeight,
    dropTargetFolderId,
    folder,
    level = 0,
    movingAttachmentIds = new Set(),
    syncingFolderIds = new Set(),
    onDragEnd,
    onDragStart,
    onDropFolder,
    onDropTargetChange,
    onSelectedKeysChange,
    renderFiles,
    selectedKeys,
}) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(level === 0);
    const folderAttachment = folder.attachment;
    const folderId = folderAttachment?.id;
    const isDragging = Boolean(folderId && draggedAttachmentId === folderId);
    const isMoving = Boolean(folderId && movingAttachmentIds.has(folderId));
    const isSyncing = isMoving || Boolean(folderId && syncingFolderIds.has(folderId));
    const isDropTarget = Boolean(folderId && dropTargetFolderId === folderId);
    const folderItemCount = getFolderItemCount(folder);
    const folderFileIds = useMemo(() => getFolderFileIds(folder), [folder]);
    const selectedFolderFileCount = folderFileIds.filter((id) => selectedKeys.has(id)).length;
    const allFolderFilesSelected = folderFileIds.length > 0 && selectedFolderFileCount === folderFileIds.length;
    const someFolderFilesSelected = selectedFolderFileCount > 0 && !allFolderFilesSelected;
    const rawSectionStyle: CSSProperties = level ? { margin: '0.375rem 0 0 0.75rem' } : { borderRadius: 0, margin: 0 };
    const sectionStyle = getDropZoneIdleStyle(rawSectionStyle, token);
    const headerBaseStyle: CSSProperties = { padding: '0 1rem' };
    const headerStyle = isDragging || isMoving ? getDragActiveStyle(headerBaseStyle, token) : headerBaseStyle;
    const canDrop = useCallback(
        (event: DragEvent<HTMLElement>) => Boolean(folderAttachment && canDropInFolder(event, folderAttachment)),
        [canDropInFolder, folderAttachment],
    );
    const handleDrop = useCallback(
        (event: DragEvent<HTMLElement>) => {
            if (folderAttachment) {
                onDropFolder(event, folderAttachment);
            }
        },
        [folderAttachment, onDropFolder],
    );
    const handleDropActiveChange = useCallback(
        (active: boolean) => {
            if (folderId) {
                onDropTargetChange(folderId, active);

                if (active) {
                    setOpen(true);
                }
            }
        },
        [folderId, onDropTargetChange],
    );
    const dropZoneHandlers = useNativeDropZone<HTMLElement>({
        active: isDropTarget,
        canDrop,
        disabled: !folderAttachment || !draggedAttachmentId,
        onActiveChange: handleDropActiveChange,
        onDrop: handleDrop,
    });

    return (
        <CompactCollapsible
            bordered
            contentStyle={{ padding: 0 }}
            destroyOnHidden
            edgeToEdge={level === 0}
            headerExtra={
                folderAttachment ? (
                    <AttachmentItemActions
                        attachment={folderAttachment}
                        folderOpen={open}
                        onFolderOpenChange={setOpen}
                    />
                ) : undefined
            }
            headerPrefix={
                folderAttachment ? (
                    <DragHandle
                        className='attachment-drag-handle'
                        disabled={isMoving}
                        isDragging={isDragging || isMoving}
                        label={`Move ${folder.name}`}
                    />
                ) : undefined
            }
            headerSelection={
                <span
                    draggable={false}
                    onDragStart={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                >
                    <Checkbox
                        aria-label={`${allFolderFilesSelected ? 'Deselect' : 'Select'} all attachments in ${folder.name}`}
                        checked={allFolderFilesSelected}
                        disabled={folderFileIds.length === 0}
                        indeterminate={someFolderFilesSelected}
                        onChange={(event) => {
                            const nextSelectedKeys = new Set(selectedKeys);

                            folderFileIds.forEach((id) => {
                                if (event.target.checked) {
                                    nextSelectedKeys.add(id);
                                } else {
                                    nextSelectedKeys.delete(id);
                                }
                            });
                            onSelectedKeysChange(nextSelectedKeys);
                        }}
                        onClick={(event) => event.stopPropagation()}
                    />
                </span>
            }
            headerSelectionActive={selectedFolderFileCount > 0}
            headerDataAttributes={
                folderAttachment ? { 'data-attachment-drag-preview-source': folderAttachment.id } : undefined
            }
            headerProps={
                folderAttachment
                    ? {
                          draggable: !isMoving,
                          onDragEnd,
                          onDragStart: (event) => onDragStart(event, folderAttachment),
                      }
                    : undefined
            }
            headerPrefixHoverOnly={!isDragging && !isMoving}
            headerStyle={headerStyle}
            icon={<WorkspaceFolderGlyph variant={open ? 'open' : 'default'} />}
            label={
                <span>
                    {folder.name}
                    {isSyncing && <SyncingSmallTag />}
                </span>
            }
            open={open}
            sectionProps={folderAttachment ? dropZoneHandlers : undefined}
            sectionStyle={sectionStyle}
            onOpenChange={setOpen}
        >
            {renderFiles(folder.files)}
            {isDropTarget && folderItemCount > 0 ? (
                <DropPlaceholder
                    data-attachment-drop-placeholder='true'
                    style={{ height: draggedAttachmentHeight ?? '3.75rem', margin: '0.25rem 0' }}
                />
            ) : null}
            {Array.from(folder.folders.values()).map((child) => (
                <AttachmentFolderSection
                    key={child.key}
                    canDropInFolder={canDropInFolder}
                    draggedAttachmentId={draggedAttachmentId}
                    draggedAttachmentHeight={draggedAttachmentHeight}
                    dropTargetFolderId={dropTargetFolderId}
                    folder={child}
                    level={level + 1}
                    movingAttachmentIds={movingAttachmentIds}
                    syncingFolderIds={syncingFolderIds}
                    renderFiles={renderFiles}
                    onDragEnd={onDragEnd}
                    onDragStart={onDragStart}
                    onDropFolder={onDropFolder}
                    onDropTargetChange={onDropTargetChange}
                    onSelectedKeysChange={onSelectedKeysChange}
                    selectedKeys={selectedKeys}
                />
            ))}
            {folderItemCount === 0 ? (
                <div
                    data-attachment-empty-folder-drop-target={isDropTarget ? 'active' : undefined}
                    style={
                        isDropTarget
                            ? getDropZoneStyle({ margin: '1.25rem 1rem' }, token)
                            : getDropZoneIdleStyle({ margin: '1.25rem 1rem' }, token)
                    }
                >
                    <EmptyView
                        description='Drag files into this folder to keep its attachments organized.'
                        height={144}
                        icon={<TbFolderCancel size='1.25rem' />}
                        label='Empty Folder'
                    />
                </div>
            ) : null}
        </CompactCollapsible>
    );
};

export default AttachmentFolderSection;
